// Ghost Content API adapter.
// Pure function: opts in, Post[] out. Validates with Zod.
// Fails open: returns [] on any error (UI falls back to manual posts).

import { z } from 'zod';
import { PostSchema, type Post } from '@lib/schemas/post';
import { getClientEnv, isConfigured } from '@lib/utils/env';
import { formatDate } from '@lib/utils/formatDate';

// Ghost response shape — only the fields we read.
const GhostTagSchema = z.object({ name: z.string() });
const GhostPostSchema = z.object({
  title: z.string(),
  excerpt: z.string().optional(),
  custom_excerpt: z.string().nullable().optional(),
  published_at: z.string().optional(),
  reading_time: z.number().optional(),
  url: z.string(),
  feature_image: z.string().nullable().optional(),
  tags: z.array(GhostTagSchema).optional(),
});
const GhostResponseSchema = z.object({
  posts: z.array(GhostPostSchema),
});

export interface FetchGhostOpts {
  limit?: number;
  signal?: AbortSignal;
}

export async function fetchGhostPosts(opts: FetchGhostOpts = {}): Promise<Post[]> {
  const { GHOST_URL, GHOST_CONTENT_API_KEY } = getClientEnv();
  if (!isConfigured(GHOST_URL) || !isConfigured(GHOST_CONTENT_API_KEY)) {
    return [];
  }

  const limit = opts.limit ?? 6;
  const endpoint =
    `${GHOST_URL.replace(/\/$/, '')}/ghost/api/content/posts/` +
    `?key=${encodeURIComponent(GHOST_CONTENT_API_KEY)}` +
    `&limit=${limit}` +
    `&include=tags` +
    `&fields=title,excerpt,custom_excerpt,published_at,reading_time,url,feature_image`;

  try {
    const res = await fetch(endpoint, {
      headers: { 'Accept-Version': 'v5.0' },
      signal: opts.signal,
    });
    if (!res.ok) {
      console.warn(`[ghost] non-ok response: ${res.status} ${res.statusText}`);
      return [];
    }
    const raw = await res.json();
    const parsed = GhostResponseSchema.safeParse(raw);
    if (!parsed.success) {
      console.warn('[ghost] response failed schema validation:', parsed.error.message);
      return [];
    }

    return parsed.data.posts.map((p): Post => {
      const post = PostSchema.parse({
        tag: p.tags?.[0]?.name ?? 'Post',
        title: p.title,
        excerpt: p.custom_excerpt ?? p.excerpt ?? '',
        date: formatDate(p.published_at),
        read: typeof p.reading_time === 'number' ? `${p.reading_time} min` : undefined,
        href: p.url,
        thumbnail: p.feature_image ?? undefined,
        source: 'ghost' as const,
      });
      return post;
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return [];
    console.warn('[ghost] fetch failed:', err instanceof Error ? err.message : err);
    return [];
  }
}
